import { Component, DestroyRef, inject, Injector, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SelectionModel } from '@angular/cdk/collections';
import { TranslatePipe } from '@ngx-translate/core';
import { CollaboratorDetails, DropDownItem } from '../../../../../models/dtos';
import { SolPreferencesService } from '../../../../../services/preferences/sol-preferences.service';
import { CollaboratorTableData } from '../../../../../models/preferences/CollaboratorTableData';
import { IAddCollaboratorDialogData } from '../../../../../models/preferences/IAddCollaboratorDialogData';
import { AddCollaboratorDialogComponent } from '../../../../dialogs/common/add-collaborator-dialog/add-collaborator-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CONSTANTS } from '../../../../../Constants/Constants';
import { CommonMetaDataService } from '../../../../../services/common-meta-data.service';
import { emailsMatch } from '../../../../../services/collab-common-functions';


@Component({
  selector: 'app-sol-preferences-collaborators',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatTableModule,
    TranslatePipe
  ],
  templateUrl: './sol-preferences-collaborators.component.html',
  styleUrls: ['./sol-preferences-collaborators.component.scss'],
})
export class SolPreferencesCollaboratorsComponent implements OnInit {

  // --- Top table: Collaborators ---
  collaboratorsColumns: string[] = ['role', 'collaborator', 'email', 'access'];
  collaboratorsDataSource = new MatTableDataSource<CollaboratorTableData>();
  collaboratorsSelection = new SelectionModel<CollaboratorTableData>(false, []);

  // --- Bottom table: Available Collaborators ---
  availableColumns: string[] = ['role', 'collaborator', 'email', 'access', 'delete'];
  availableDataSource = new MatTableDataSource<CollaboratorTableData>();
  availableSelection = new SelectionModel<CollaboratorTableData>(false, []);
  collaborators: CollaboratorDetails[];
  availableCollaborators: CollaboratorDetails[];
  private destroyRef = inject(DestroyRef);

  constructor(
    private readonly preferencesService: SolPreferencesService,
    private readonly dialogRef: MatDialog,
    private readonly commonMetaDataService: CommonMetaDataService) {
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm(): void {
    const userPreferences = this.preferencesService.getUserPreferences();
    this.collaborators = userPreferences.collaboratorPreferences ? userPreferences.collaboratorPreferences.collaborators : [];
    this.availableCollaborators = userPreferences.collaboratorPreferences ? userPreferences.collaboratorPreferences.availableCollaborators : [];
    this.collaboratorsDataSource.data = this.collaborators.map(c => ({
      name: c.name,
      email: c.email,
      roleId: c.roleId,
      canEdit: c.canEdit,
      isPrimaryUser: c.isPrimaryUser,
      roleName: this.mapRoleIdToRoleName(c.roleId)
    }));
    this.availableDataSource.data = this.availableCollaborators.map(c => ({
      name: c.name,
      email: c.email,
      roleId: c.roleId,
      canEdit: c.canEdit,
      isPrimaryUser: c.isPrimaryUser,
      roleName: this.mapRoleIdToRoleName(c.roleId)
    }));
  }

  // ---- Row click handlers ----

  onCollaboratorRowClick(row: CollaboratorTableData): void {
    this.collaboratorsSelection.toggle(row);
    // Clear the other table's selection so only one table has a selection at a time
    this.availableSelection.clear();
  }

  onAvailableRowClick(row: CollaboratorTableData): void {
    this.availableSelection.toggle(row);
    this.collaboratorsSelection.clear();
  }

  // ---- Button states ----

  get isAddEnabled(): boolean {
    return !this.availableSelection.isEmpty() && this.collaboratorsDataSource.data.length < CONSTANTS.MAX_COLLABORATORS;
  }

  get isRemoveEnabled(): boolean {
    return !this.collaboratorsSelection.isEmpty();
  }

  get isAddCollaboratorEnabled(): boolean {
    return this.availableDataSource.data.length < CONSTANTS.MAX_AVAILABLE_COLLABORATORS;
  }

  // ---- Actions ----

  addToCollaborators(): void {
    if (this.availableSelection.isEmpty()) {
      return;
    }

    const selected = this.availableSelection.selected[0];

    // Move from available → collaborators
    this.collaboratorsDataSource.data = [...this.collaboratorsDataSource.data, selected];
    this.availableDataSource.data = this.availableDataSource.data.filter(r => r !== selected);
    this.availableSelection.clear();
    this.syncToService();
  }

  removeFromCollaborators(): void {
    if (this.collaboratorsSelection.isEmpty()) {
      return;
    }

    const selected = this.collaboratorsSelection.selected[0];

    // Move from collaborators → available
    this.availableDataSource.data = [...this.availableDataSource.data, selected];
    this.collaboratorsDataSource.data = this.collaboratorsDataSource.data.filter(r => r !== selected);
    this.collaboratorsSelection.clear();
    this.syncToService();
  }

  deleteAvailableCollaborator(row: CollaboratorTableData): void {
    this.availableDataSource.data = this.availableDataSource.data.filter(r => r !== row);

    if (this.availableSelection.isSelected(row)) {
      this.availableSelection.clear();
    }
    this.syncToService();
  }

  addCollaborator(): void {
    const dialogData: IAddCollaboratorDialogData = {
      collaboratorRoles: this.commonMetaDataService.getMetaData()?.collaboratorRoles ?? [],
      excludedCollaboratorRoleIds: this.commonMetaDataService.getMetaData()?.collaboratorRoleIdsToExclude ?? [],
      alreadyAddedCollaborators: [...this.collaboratorsDataSource.data, ...this.availableDataSource.data].map(item => ({
        name: item.name,
        email: item.email,
        roleId: item.roleId ?? 0,
        canEdit: item.canEdit,
        isPrimaryUser: item.isPrimaryUser,
        role: item.roleName
      }) as CollaboratorDetails),
      isCalledFromPreferences: true
    };
    const dialog = this.dialogRef.open(AddCollaboratorDialogComponent, {
      width: '455px',
      data: {
        ...dialogData,
        isCalledFromPreferences: true
      },
      disableClose: true
    });
    dialog.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) {
          this.addCollaboratorToAvailableTable(result);
        }
      });
  }

  private getAvailableReviewers(): CollaboratorDetails[] {
    return this.availableDataSource.data.map(item => ({
      name: item.name,
      email: item.email,
      roleId: item.roleId ?? 0,
      canEdit: item.canEdit,
      isPrimaryUser: item.isPrimaryUser,
      role: item.roleName
    }) as CollaboratorDetails);
  }

  private addCollaboratorToAvailableTable(collaboratorData: CollaboratorDetails): void {
    const isAlreadyPresent = [...this.collaboratorsDataSource.data, ...this.availableDataSource.data]
      .some(item => emailsMatch(item.email, collaboratorData.email));
    if (isAlreadyPresent) {
      return;
    }

    const roleName = this.mapRoleIdToRoleName(collaboratorData.roleId);

    const collaboratorTableData: CollaboratorTableData = {
      ...collaboratorData,
      roleName
    };

    this.availableDataSource.data = [...this.availableDataSource.data, collaboratorTableData];
    this.syncToService();
  }

  private mapRoleIdToRoleName(roleId: number | null): string {
    const role = this.commonMetaDataService.getMetaData()?.collaboratorRoles.find(item => item.id === roleId);
    return role ? role.description : '';
  }

  toggleAccess(row: CollaboratorTableData): void {
    row.canEdit = !row.canEdit;
    this.syncToService();
  }

  /** Push current table state back into the shared service. */
  private syncToService(): void {
    const mapToDetails = (rows: CollaboratorTableData[]): CollaboratorDetails[] =>
      rows.map(r => ({
        roleId: r.roleId ?? 0,
        role: r.roleName,
        name: r.name,
        email: r.email,
        canEdit: r.canEdit,
        userId: '',
        isPrimaryUser: r.isPrimaryUser
      }));

    const userPreferences = this.preferencesService.getUserPreferences();
    userPreferences.collaboratorPreferences = {
      collaborators: mapToDetails(this.collaboratorsDataSource.data),
      availableCollaborators: mapToDetails(this.availableDataSource.data)
    };
  }
}
